import { createSelector } from 'reselect';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
const EMPTY = '-';
const maxLineUpRow = 9;

const cleanupData = (data) => {
    return data.map((item) => {
        if (item?.stats) {
            const temp = {};
            for (const p in item?.stats) {
                const currentState = item?.stats[p];
                temp[p] = (currentState === 0) ? currentState.toString() : currentState;
            }
            return { ...item, ...{ stats: temp } };
        } else {
            return item;
        }
    });
};

const getData = (batter, temp, language, id) => {
    return {
        avg: (typeof temp?.avg !== 'undefined') ? temp.avg : EMPTY,
        hr: (typeof temp?.homeRuns !== 'undefined') ? temp.homeRuns : EMPTY,
        sb: (typeof temp?.stolenBases !== 'undefined') ? temp.stolenBases : EMPTY,
        rbi: (typeof temp?.rbi !== 'undefined') ? temp.rbi : EMPTY,
        ops: (typeof temp?.ops !== 'undefined') ? temp.ops : EMPTY,
        b: (typeof batter?.person?.batSide?.code !== 'undefined') ? batter?.person?.batSide?.code : EMPTY,
        name: batter?.person?.boxscoreName || '',
        position: batter?.displayPositions || '',
        order: batter?.battingOrder?.charAt(0),
        playerId: id
    };
};

const getLineupData = (state, type) => {
    const {
        previewlineup: {
            gumbo: {
                gamePk,
                teams = {},
                boxscore = {}
            } = {},
            awayLineup = {},
            homeLineup = {},
            schedule = {}
        } = {},
        config: {
            language = 'en'
        }
    } = state;

    const teamName = boxscore?.teams?.[type]?.team?.abbreviation || '';
    const scheduleTeams = schedule?.teams;
    const boxscoreData = boxscore?.[type];
    const boxscoreBattersAvailable = !!(boxscoreData?.batters?.length);
    const scheduleLineup = scheduleTeams?.[type] && scheduleTeams?.[type]?.lineup;
    const lineupList = boxscoreBattersAvailable ? teams?.[type]?.batters : scheduleLineup?.length ? scheduleLineup : [];
    const scheduleGamePkMatch = (gamePk === schedule?.gamePk);
    const lineupData = {
        home: homeLineup,
        away: awayLineup
    };

    if (scheduleGamePkMatch && lineupList.length === 0) {
        return {
            data: {
                teamName,
                players: []
            },
            hasLineup: false,
            error: true
        };
    }

    const gamePkMatch = (scheduleGamePkMatch && gamePk === lineupData[type]?.gamePk);

    let hasLineup = false;
    const typeData = {
        teamName,
        players: null
    };

    if (gamePkMatch && lineupData?.[type]?.data) {
        lineupList.forEach((batter, index) => {
            const id = batter?.person?.id;
            const bo = batter.battingOrder;
            const vaildBatter = bo && bo.slice(-2) === '00';
            if (vaildBatter) {
                const temp = lineupData[type]?.data?.playersById?.[id];
                const playerInfo = boxscore?.teams?.[type]?.playerById[id] || {};

                if (temp && vaildBatter) {
                    if (!typeData?.players) {
                        typeData.players = [];
                    }
                    typeData.players.push(getData(playerInfo, temp, language, id));
                    hasLineup = true;
                }
            }
        });
        const validPlayerRows = typeData?.players?.length || 0;
        const diff = maxLineUpRow - validPlayerRows;

        // we pnly need to do this when we need to.
        if (typeData?.players?.length > 0 && diff > 0) {
            for (let i = validPlayerRows; i <= diff + 1; i++) {
                typeData.players.push({});
            }
        }

        return {
            data: typeData,
            hasLineup,
            error: false
        };
    }

    return {};
};

const getHomeLineup = (state) => {
    return getLineupData(state, 'home');
};

const getAwayLineup = (state) => {
    return getLineupData(state, 'away');
};

const getMatchup = (state) => {
    const {
        previewmatchup = {},
        config: {
            language = 'en'
        }
    } = state;
    const currentGamePk = previewmatchup?.gumboGamePk;
    const {
        isDataLoaded,
        away = [],
        home = [],
        probables: {
            homeProbableLastName,
            awayProbableLastName,
            homeAbbreviation,
            awayAbbreviation
        } = {}
    } = previewmatchup || {};

    const gamePkMatch = (currentGamePk && currentGamePk === previewmatchup?.gamePk);

    return gamePkMatch
        ? {
            away: {
                data: cleanupData(away),
                team: awayAbbreviation,
                probables: homeProbableLastName
            },
            home: {
                data: cleanupData(home),
                team: homeAbbreviation,
                probables: awayProbableLastName
            },
            language,
            isDataLoaded
        }
        : {};
};

export const getLineupMatchupState = createSelector(
    [getAwayLineup, getHomeLineup, getMatchup],
    (awaylineup, homelineup, matchup) => {
        return {
            awaylineup,
            homelineup,
            matchup
        };
    }
);

export const getMatchupState = createSelector(
    [getMatchup],
    (matchup) => {
        return {
            matchup
        };
    }
);

export const getTabsState = createSelector(
    [getAwayLineup, getHomeLineup, getMatchup],
    (awaylineup, homelineup, matchup) => {
        const tabsData = [];
        const translate = useTranslate();

        const matchupTab = {
            text: 'Matchups',
            name: translate('label_matchups', 'Matchups'),
            key: 'label_matchups',
            id: 'matchups'
        };

        const lineupTab = {
            text: 'Lineups',
            name: translate('label_lineups', 'Lineups'),
            key: 'label_lineups',
            id: 'lineups'
        };

        const hasLineups = awaylineup?.hasLineup || homelineup?.hasLineup;
        const hasMatchup = matchup?.away || matchup?.home;

        if (hasLineups) {
            tabsData.push(lineupTab);
        }

        if (hasMatchup) {
            tabsData.push(matchupTab);
        }

        return {
            tabsData
        };
    }
);
